// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { AppStatus, GetAppStatusRequestState } from "../../utils/types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { SnackMessage } from "../../config/constant";
import { APIService } from "../../utils/apiService";
import { AppConfig } from "../../config/config";
import { enqueueSnackbarMessage } from "../commonSlice/common";

const initialState: GetAppStatusRequestState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  response: {
    isShowMaintenanceBanner: null,
    maintenanceBannerTitle: null,
    maintenanceBannerContent: null,
    isAppInMaintenanceState: null,
    isExamResumptionEnabled: false,
  },
  backgroundProcess: false,
  backgroundProcessMessage: null,
};

export const getStatus = createAsyncThunk(
  "app/getStatus",
  async (_, { dispatch }) => {
    return new Promise<{ status: AppStatus }>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.getAppStatus)
        .then((resp) => {
          if (resp.status === 200) {
            const parsedStatus: AppStatus = resp.data.reduce(
              (
                acc: Record<string, any>,
                item: { ConfigKey: string; value: string; type: string }
              ) => {
                if (item.ConfigKey) {
                  acc[item.ConfigKey] =
                    item.type === "boolean"
                      ? item.value === "true"
                      : item.value;
                } else {
                  console.warn("Invalid ConfigKey in response item:", item);
                }
                return acc;
              },
              {} as Record<string, any>
            );
            resolve({ status: parsedStatus });
          } else {
            reject("Something went wrong");
          }
        })
        .catch((error: Error) => {
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.appStatus,
              type: "error",
            })
          );
          reject(error);
        });
    });
  }
);

export const getAdminStatus = createAsyncThunk(
  "app/getAdminStatus",
  async (_, { dispatch }) => {
    return new Promise<{ isAppInMaintenanceState: boolean }>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.getAdminConfigs)
        .then((resp) => {
          if (resp.status === 200) {
            resolve({ isAppInMaintenanceState: resp.data.isAppInMaintenanceState });
          } else {
            reject("Something went wrong");
          }
        })
        .catch((error: Error) => {
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.appStatus,
              type: "error",
            })
          );
          reject(error);
        });
    });
  }
);

// Combined thunk that waits for APIService and dispatches both calls
export const getAppData = createAsyncThunk(
  "app/getAppData",
  (_, { dispatch }) => {
    return new Promise<{ status: AppStatus }>((resolve, reject) => {
      const waitForAPIInstance = (retries = 5) => {
        const api = APIService.getInstance();
        if (!api && retries > 0) {
          setTimeout(() => waitForAPIInstance(retries - 1), 100);
        } else if (!api) {
          reject("APIService not initialized");
        } else {
          Promise.all([
            dispatch(getStatus()).then((res: any) => res?.payload),
            dispatch(getAdminStatus()).then((res: any) => res?.payload),
          ])
            .then(([appStatus, adminStatus]) => {
              if (appStatus && adminStatus) {
                const mergedStatus: AppStatus = {
                  ...appStatus.status,
                  isAppInMaintenanceState: adminStatus.isAppInMaintenanceState,
                };
                resolve({ status: mergedStatus });
              } else {
                reject("Missing data from one or more APIs");
              }
            })
            .catch((error) => {
              dispatch(
                enqueueSnackbarMessage({
                  message: SnackMessage.error.appStatus,
                  type: "error",
                })
              );
              reject(error);
            });
        }
      };
      waitForAPIInstance();
    });
  }
);

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {},
  extraReducers:  (builder) => {
    builder
    .addCase(getAppData.pending, (state, action) => {
      state.stateMessage = "Loading App Status";
      state.state = "loading";
      state.backgroundProcess = true;
      state.backgroundProcessMessage = "Loading...";
    })
    .addCase(getAppData.fulfilled, (state, action) => {
      state.response = action.payload.status;
      state.state = "success";
      state.backgroundProcess = false;
    })
    .addCase(getAppData.rejected, (state, action) => {
      state.state = "failed";
      state.backgroundProcess = false;
    });
  },
});

export default appSlice.reducer;

